import {makeStyles} from "@material-ui/core/styles";
import clsx from "clsx";
import * as PropTypes from "prop-types";
import UserEvent from "../../core/UserEvent";
import {createRef, useState} from "react";
import fileIO from "../../core/fileIO";
import lang from "../../core/lang";
import usePrompt from "../../hook/usePrompt";

const useStyles = makeStyles(theme => ({
    root: {
        width: props => props.width,
        height: props => props.height,
        cursor: props => props.disabled ? 'not-allowed' : 'pointer',
        backgroundColor: 'transparent',
        overflow: 'hidden'
    },
    input: {
        display: 'none'
    }
}));

function FileDrop(props) {
    const {fullWidth, fullHeight, className, multiple, accept, disabled, children} = props;
    const width = fullWidth ? '100%' : props.width;
    const height = fullHeight ? '100%' : props.height;
    const classes = useStyles({width, height, disabled});
    const input = createRef();
    const [timestamp, setTimestamp] = useState(new Date());


    const change = async files => {
        const descriptors = [];
        // A FileList is an array-like object that represents a collection of File objects.
        // A FileList has the length property. However, it's not an actual array.
        for(let i=0; i<files.length; i++) {
            // transform the file to file descriptor to make it easier to store in Redux.
            const descriptor = await fileIO.encode(files[i]);
            descriptors.push(descriptor);
        }

        props.onChange && props.onChange(new UserEvent({
            files: Array.from(files),
            file: files[0],
            descriptors,
            descriptor: descriptors[0]
        }))
        // 強迫 file input 重新產生，避免 file input 無法連續選擇相同檔案的問題。
        setTimestamp(new Date());
    }

    const handleInputChange = async event => {
        const currentTarget = event.currentTarget;
        const files = currentTarget.files;

        if(files.length===0) return;

        await change(files);
    }

    const handleClick = event => {
        event.stopPropagation();
        if(disabled) return;
        input.current.click();
        props.onClick && props.onClick(new UserEvent({}, event));
    }


    const handleDrop = async event => {
        event.preventDefault();
        event.stopPropagation()

        let files = event.dataTransfer.files;
        if (files.length === 0) return;

        await change(files);
    }

    const handleDragOver = event => {
        event.preventDefault();
        event.stopPropagation();

        let items = event.dataTransfer.items;

        if(disabled||!testTransferItems(items)) {
            event.dataTransfer.dropEffect = 'none';
        } else {
            event.dataTransfer.dropEffect = 'copy';
        }
    }

    const testTransferItems = (items) => {
        if(lang.isEmpty(accept)) return true;

        const acceptTypes = lang.isArray(accept) ? accept : [accept];

        const test = lang.every(items, item => {
            if(item.kind!=='file') return false;

            const file = item;
            const test = acceptTypes.some(acceptType => {
                const extension = fileIO.getFileExtension(item);
                const type = lang.isEmpty(file.type) ? fileIO.getMediaTypeByExtension(extension) : file.type;
                let test;
                if(acceptType.startsWith('.')) {
                    test = (extension===acceptType);
                } else if(acceptType.indexOf('*')===-1) {
                    test = (type===acceptType);
                } else {
                    const pattern = new RegExp(acceptType);
                    test = pattern.test(type);
                }
                return test;
            });
            return test;
        });

        return test;
    }

    return (
        <div className={clsx(classes.root, className, 'appfuse-fileDrop')}
             onDrop={handleDrop} onDragOver={handleDragOver} onClick={handleClick} >
            {children}
            <input className={classes.input} ref={input} type="file" key={timestamp}
                   multiple={multiple} accept={accept}
                   disabled={disabled}
                   onChange={handleInputChange} />
        </div>
    )
}

FileDrop.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(File),
        PropTypes.object,
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.arrayOf(PropTypes.instanceOf(File)),
        PropTypes.arrayOf(PropTypes.object)
    ]),
    onChange: PropTypes.func,
    onError: PropTypes.func,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    fullWidth: PropTypes.bool,
    fullHeight: PropTypes.bool,
    multiple: PropTypes.bool,
    accept: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string)
    ]),
    width: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    height: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ])
};

FileDrop.defaultProps = {
    multiple:  false
};

export default FileDrop;